import { getSendNodeResources } from 'api/WorkflowsAPI';
import { TemplateId } from 'modules/domains';
import { GqlUpsertTemplateInvalidContentFailureTypes as TemplateErrorType } from 'modules/templateEditor/common/types';
import { ChannelNodeTypeMap } from 'modules/workflows/constants';
import {
  BackendNode,
  BackendWFNode,
  BackendWorkflow,
  ChildNode,
  Node,
  NodeId,
} from 'modules/workflows/types';
import { parseBackendNodes, parseEdge, parseWorkflowForSaving } from 'modules/workflows/utils';
import { WorkflowState } from '../../ducks.types';
import { ValidateClonedJourneySagaErrorDetail } from '../types';

export const getParsedNodeData = (workflow: BackendWorkflow, wfState: WorkflowState) => {
  const parsedNodes = parseBackendNodes(workflow, { ...wfState, workflow });
  const parsedEdges = (workflow?.edges || []).map(parseEdge);
  const updatedWorkflow = parseWorkflowForSaving({
    ...workflow,
    workflowReference: null,
    nodes: parsedNodes,
    edges: parsedEdges,
  });

  return updatedWorkflow;
};

export const createChildResourceEffect = (
  childNode: ChildNode,
  effect: (...args: unknown[]) => void,
  fetchEvents: () => Promise<any>,
) => {
  const childNodeCampaignId = childNode?.nodeData?.campaignId || undefined;
  const channel = ChannelNodeTypeMap[childNode.type].toLowerCase();
  return {
    campaign: effect(getSendNodeResources, { campaignId: childNodeCampaignId, channel }),
    customEvents: effect(fetchEvents),
  };
};

export const mapErrorsToNodeId = (
  errors: { value: any; key: TemplateId }[],
  templateIds: Map<TemplateId, number>,
): { [nodeId: number]: any } => {
  return errors.reduce((acc, err) => {
    const nodeId = templateIds.get(err.key) as number;
    return {
      ...acc,
      [nodeId]: err.value,
    };
  }, {});
};

const SNIPPETS_ERRORS = [
  TemplateErrorType.MISSING_SNIPPETS,
  TemplateErrorType.INVALID_SNIPPET_PARAMS,
];

export const getClonedNodes = (
  workflow: BackendWorkflow,
  { nodes: clonedFromNodes }: { nodes: BackendNode[] },
  nodeToTemplateErrorsMap: Record<NodeId, any> = {},
) => {
  const { nodes = [], updatedAt = '', createdAt = '' } = workflow;

  // in case a user initially had start node filters or wait delay property
  // then chose to remove them, we want to prevent the validation messages from showing again after update
  // todo: ideally this would use the node created/updatedAt
  // which would require additional backend work
  const isNewClone = new Date(updatedAt).toLocaleString() === new Date(createdAt).toLocaleString();
  const validatedNodes: BackendNode[] = [];
  const clonedNodes: BackendNode[] = [];

  nodes.forEach((node) => {
    const { clonedFromNode } = clonedFromNodes?.find((n) => n.id === node.id) || {};

    // Only validate nodes that have been cloned
    if (!clonedFromNode) return;

    const {
      nodeData: clonedNodeData,
      nodeDataJson: clonedNodeDataJson,
      resource: clonedResource,
    } = clonedFromNode as Partial<BackendWFNode>;

    // This is not ideal but in some cases we need to handle validation on our own
    // (as is the case where some tiles can be tested/published in an "invalid" state
    // but we still want to highlight any cloning errors to the user)
    const shouldHighlight =
      clonedNodeData?.adAccountId || // Facebook tile
      clonedNodeData?.customAudience || // Facebook tile
      clonedNodeDataJson?.searchCombo || // Start tile
      (clonedNodeData?.delayUsingProperty && !node.nodeData?.delayUsingProperty) || // Wait tile
      !!node.nodeDataJson?.triggerRule?.cloningErrorsList?.length || // Hold tile
      !!clonedResource?.campaign?.conversions?.length || // Send tiles
      // ChO tiles
      clonedResource?.resources?.some((choTile) => !!choTile.campaign?.conversions?.length) ||
      // Send tiles with templates
      nodeToTemplateErrorsMap[node.id]?.some((err: { __typename: TemplateErrorType }) =>
        SNIPPETS_ERRORS.includes(err.__typename),
      );

    if (isNewClone && shouldHighlight) {
      validatedNodes.push(node);
    }
    clonedNodes.push(node);
  });

  return { validatedNodes, clonedNodes };
};

export const getUniqueClonedNodeIds = (
  nodes: (Node | BackendNode | ValidateClonedJourneySagaErrorDetail)[],
) => {
  // backend occasionally returns multiple error messages for one tile
  // so we want only unique IDs
  const errors = nodes?.reduce((acc: NodeId[], node) => {
    const nodeId =
      (node as ValidateClonedJourneySagaErrorDetail).data?.nodeId ||
      (node as Node | BackendNode).id;
    return nodeId && !acc.includes(nodeId?.toString()) ? [...acc, nodeId.toString()] : acc;
  }, []);

  return errors.sort((a: string, b: string) => (b as unknown as number) - (a as unknown as number));
};
